import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2, Lock, Eye, EyeOff } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useSite } from "../../context/SiteContext";
import { api, formatErr } from "../../lib/api";
import { Field, inputCls } from "../kit";

const TYPES = [["text", "Short text"], ["textarea", "Long text"], ["number", "Number"], ["email", "Email"], ["date", "Date"], ["select", "Dropdown"], ["checkboxes", "Checkboxes (multiple)"]];
const CORE_TYPE = { service: "Service picker (from your services)", service_option: "Service options (from selected service)", file: "Screenshot upload" };

function FieldCard({ f, i, n, update, move, remove }) {
  const locked = f.locked;
  const hasOptions = ["select", "checkboxes"].includes(f.type);
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${f.hidden ? "border-white/10 opacity-60" : "border-[#007bff]/30"}`} data-testid={`qf-card-${f.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs px-2 py-0.5 rounded bg-[#007bff]/20 text-[#5cc8ff]">#{i + 1}</span>
        <span className="text-xs text-slate-400">{f.core ? "Built-in" : "Custom"} · {CORE_TYPE[f.type] || TYPES.find((t) => t[0] === f.type)?.[1]}</span>
        {locked && <span className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> required for quotes</span>}
        <div className="ml-auto flex gap-1">
          <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30" data-testid={`qf-up-${f.key}`}><ArrowUp className="w-4 h-4" /></button>
          <button disabled={i === n - 1} onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30" data-testid={`qf-down-${f.key}`}><ArrowDown className="w-4 h-4" /></button>
          {!locked && f.core && <button onClick={() => update({ hidden: !f.hidden })} className="p-1.5 rounded hover:bg-white/5" title={f.hidden ? "Show" : "Hide"} data-testid={`qf-hide-${f.key}`}>{f.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>}
          {!f.core && <button onClick={() => remove(i)} className="p-1.5 rounded hover:bg-white/5 text-rose-300" data-testid={`qf-remove-${f.key}`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Question / label"><Input value={f.label} onChange={(e) => update({ label: e.target.value })} className={inputCls} data-testid={`qf-label-${f.key}`} /></Field>
        <Field label="Placeholder"><Input value={f.placeholder || ""} onChange={(e) => update({ placeholder: e.target.value })} className={inputCls} /></Field>
        {!f.core && (
          <Field label="Answer type">
            <Select value={f.type} onValueChange={(v) => update({ type: v })}>
              <SelectTrigger className={inputCls} data-testid={`qf-type-${f.key}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#060d20]">{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Help text (optional)"><Input value={f.help || ""} onChange={(e) => update({ help: e.target.value })} className={inputCls} /></Field>
        {hasOptions && <Field label="Choices (one per line)" full><Textarea rows={4} value={(f.options || []).join("\n")} onChange={(e) => update({ options: e.target.value.split("\n") })} className={inputCls} data-testid={`qf-options-${f.key}`} /></Field>}
        {f.service_label && <p className="sm:col-span-2 text-xs text-slate-500">When a service is chosen, this label is replaced by that service's own label (edit under Website Content → Services).</p>}
      </div>
      <div className="flex flex-wrap gap-5 text-sm">
        <label className="flex items-center gap-2"><Switch disabled={locked} checked={!!f.required} onCheckedChange={(v) => update({ required: v })} data-testid={`qf-required-${f.key}`} /> Required</label>
        <label className="flex items-center gap-2"><Switch checked={f.width === "full"} onCheckedChange={(v) => update({ width: v ? "full" : "half" })} /> Full width</label>
      </div>
    </div>
  );
}

export function QuoteFormBuilder() {
  const { site, reload } = useSite();
  const orig = site?.content?.quote_form?.fields || [];
  const [fields, setFields] = useState(orig);
  useEffect(() => {
    setFields(site?.content?.quote_form?.fields || []);
  }, [site]);
  const update = (i) => (patch) => setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, d) => setFields((fs) => {
    const a = [...fs];
    [a[i], a[i + d]] = [a[i + d], a[i]];
    return a;
  });
  const remove = (i) => window.confirm("Remove this question?") && setFields((fs) => fs.filter((_, j) => j !== i));
  const add = () => setFields((fs) => [...fs, { key: `custom_${Date.now().toString(36)}`, type: "text", label: "New question", placeholder: "", required: false, width: "half", core: false, options: [] }]);
  const save = async () => {
    if (fields.some((f) => !f.label.trim())) return toast.error("Every question needs a label");
    const clean = fields.map((f) => ({ ...f, options: (f.options || []).map((o) => o.trim()).filter(Boolean) }));
    try {
      await api.put("/admin-content/quote_form", { fields: clean });
      await reload();
      toast.success("Quotation form saved — live on the website");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="card-neon p-5 sm:p-6 space-y-4" data-testid="quote-form-builder">
      <p className="text-sm text-slate-400">Edit, reorder, hide or add questions. Built-in questions feed the quotation details; custom questions appear in each quotation and order as extra answers.</p>
      {fields.map((f, i) => <FieldCard key={f.key} f={f} i={i} n={fields.length} update={update(i)} move={move} remove={remove} />)}
      <button onClick={add} className="btn-ghost !py-2" data-testid="qf-add-btn"><Plus className="w-4 h-4" /> Add question</button>
      <div className="flex gap-2 pt-2 border-t border-white/10">
        <button onClick={save} className="btn-electric !py-2" data-testid="qf-save-btn">Save</button>
        <button onClick={() => setFields(orig)} className="btn-ghost !py-2" data-testid="qf-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}
